const LEAD_CAPTURE_SUBMITTED_KEY = "LEAD_CAPTURE_SUBMITTED"
const LEAD_CAPTURE_DATA_KEY = "LEAD_CAPTURE_DATA"
const LEAD_CAPTURE_FIRST_VISIT_KEY = "LEAD_CAPTURE_FIRST_VISIT"
const LEAD_CAPTURE_API_URL = "https://skills.slickshift.ai/api/leads"

export interface LeadFormData {
  name: string
  companyName: string
  email: string
  phone: string
}

/**
 * Check if user has already submitted lead form
 */
export function hasSubmittedLeadForm(): boolean {
  try {
    return localStorage.getItem(LEAD_CAPTURE_SUBMITTED_KEY) === "true"
  } catch {
    return false
  }
}

/**
 * Mark that user has submitted lead form
 */
export function markLeadFormSubmitted(): void {
  try {
    localStorage.setItem(LEAD_CAPTURE_SUBMITTED_KEY, "true")
  } catch {
    // Ignore localStorage errors
  }
}

/**
 * Save lead form data to localStorage for use in chat room ID generation
 */
export function saveLeadFormData(data: LeadFormData): void {
  try {
    localStorage.setItem(LEAD_CAPTURE_DATA_KEY, JSON.stringify(data))
  } catch {
    // Ignore localStorage errors
  }
}

/**
 * Get saved lead form data from localStorage
 */
export function getSavedLeadFormData(): LeadFormData | null {
  try {
    const raw = localStorage.getItem(LEAD_CAPTURE_DATA_KEY)
    if (!raw) return null
    return JSON.parse(raw) as LeadFormData
  } catch {
    return null
  }
}

/**
 * Check if this is user's first visit to planner
 */
export function isFirstVisit(): boolean {
  try {
    const visited = localStorage.getItem(LEAD_CAPTURE_FIRST_VISIT_KEY)
    if (!visited) {
      localStorage.setItem(LEAD_CAPTURE_FIRST_VISIT_KEY, "true")
      return true
    }
    return false
  } catch {
    return false
  }
}

/**
 * Check if lead modal should be shown
 * - User hasn't submitted the form yet
 * - Not in product context (anonymousXMPP or no XMPP)
 */
export function shouldShowLeadModal(): boolean {
  // Don't show in product context (logged in users)
  if (window.XMPP && !window.anonymousXMPP) {
    return false
  }

  // Don't show if already submitted
  if (hasSubmittedLeadForm()) {
    return false
  }

  return true
}

/**
 * Submit lead to skills.slickshift.ai which forwards to Close CRM
 */
export async function submitLeadToCRM(
  data: LeadFormData,
): Promise<string | false> {
  try {
    const response = await fetch(LEAD_CAPTURE_API_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        name: data.name,
        companyName: data.companyName,
        email: data.email,
        phone: data.phone,
        skill: "Inbound Map Planner",
      }),
    })

    if (!response.ok) {
      const errorText = await response.text()
      console.error("Failed to submit lead:", response.status, errorText)
      return false
    }

    const result = await response.json()
    console.log("Lead created successfully:", result.id)
    return result.id || true
  } catch (error) {
    console.error("Error submitting lead:", error)
    return false
  }
}
